import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type Package = {
  id: string;
  name: string;
  description: string | null;
  price_try_kurus: number | null;
  price_usd_cents: number | null;
  credits: number;
  duration_days: number;
  features_json: string;
  sort_order: number;
  kind: string;
};

export type ActiveAddon = {
  id: string;
  addon_type: string;
  starts_at: number;
  expires_at: number | null;
  created_at: number;
  package_id: string | null;
  package_name: string | null;
};

export function useActiveAddons() {
  return useQuery({
    queryKey: ['billing', 'addons'],
    queryFn: () => api.get<{ addons: ActiveAddon[] }>('/api/app/billing/addons'),
    staleTime: 60 * 1000,
  });
}

export type Order = {
  id: string;
  package_id: string;
  package_name: string | null;
  provider: string | null;
  amount_paid: number | null;
  currency: string | null;
  status: 'pending' | 'paid' | 'failed' | 'refunded';
  credits_granted: number | null;
  paid_at: number | null;
  created_at: number;
  cart_id: string | null;
};

export function usePackages() {
  return useQuery({
    queryKey: ['billing', 'packages'],
    queryFn: () => api.get<{ packages: Package[] }>('/api/app/billing/packages'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useOrders() {
  return useQuery({
    queryKey: ['billing', 'orders'],
    queryFn: () => api.get<{ orders: Order[] }>('/api/app/billing/orders'),
  });
}

export function useCheckout() {
  return useMutation({
    mutationFn: (package_id: string) =>
      api.post<{ order_id: string; checkout_url: string; session_id: string }>(
        '/api/app/billing/checkout',
        { package_id },
      ),
  });
}

// ---------- Platform side ----------

export type StripeStatus = {
  configured: boolean;
  enabled: boolean;
  currency: string;
  key_hint: string | null;
  webhook_secret_set: boolean;
  publishable_key: string | null;
  updated_at: number | null;
};

export function usePlatformBilling() {
  return useQuery({
    queryKey: ['platform', 'billing'],
    queryFn: () => api.get<{ stripe: StripeStatus; webhook_url: string }>('/api/platform/settings/billing'),
  });
}

export function useSavePlatformBilling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      secret_key?: string;
      webhook_secret?: string;
      publishable_key?: string;
      currency?: 'try' | 'usd' | 'eur';
      success_url?: string;
      cancel_url?: string;
      enabled?: boolean;
    }) => api.put<{ stripe: StripeStatus }>('/api/platform/settings/billing', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'billing'] }),
  });
}
